import { GameState, Team } from '../../../domain/entities';
import { ASSISTANT_MANAGER_LEVELS } from '../../../domain/constants';
import { assistantManagerService } from '../../../services/AssistantManagerService';
import { AssistantManagerBriefingService } from '../../../services/AssistantManagerBriefingService';
import { addNewsItem } from '../../../services/GameService';

export const useAssistantManagerManagement = (gameState: GameState) => {
    /**
     * Upgrade assistant manager to next level
     */
    const upgradeAssistantManager = (teamName: string): { success: boolean; message: string; updatedGameState?: GameState } => {
        const league = Object.values(gameState.leagues).find(l =>
            l.teams.some(t => t.name === teamName)
        );
        if (!league) return { success: false, message: 'Team not found' };

        const team = league.teams.find(t => t.name === teamName);
        if (!team) return { success: false, message: 'Team not found' };

        const currentLevel = team.assistantManager.level;
        if (currentLevel >= 6) return { success: false, message: 'Already at maximum level' };

        const nextLevelInfo = ASSISTANT_MANAGER_LEVELS[currentLevel];
        if (!nextLevelInfo) return { success: false, message: 'Invalid upgrade level' };

        if (team.finances.balance < nextLevelInfo.upgradeCost) {
            return { success: false, message: 'Insufficient funds for upgrade' };
        }

        // Deduct cost
        team.finances.balance -= nextLevelInfo.upgradeCost;
        team.finances.history.push({
            season: gameState.season,
            week: gameState.currentWeek,
            event: `Upgraded Assistant Manager to ${nextLevelInfo.name}`,
            amount: -nextLevelInfo.upgradeCost
        });

        // Upgrade level
        team.assistantManager.level = currentLevel + 1;

        // Generate new briefing from upgraded assistant manager
        const newBriefing = AssistantManagerBriefingService.generateWeeklyBriefing(gameState, team, league);
        if (!gameState.assistantManagerBriefingHistory) {
            gameState.assistantManagerBriefingHistory = [];
        }
        gameState.assistantManagerBriefingHistory.push(newBriefing);

        // Add news item about the upgrade
        const newManagerName = ASSISTANT_MANAGER_LEVELS[currentLevel].name;
        addNewsItem(gameState, '📧 Assistant Manager Update', `Your new ${newManagerName} has provided an assessment following their appointment.`, 'general', undefined, { type: 'assistant', title: 'Assistant Manager' });

        return {
            success: true,
            message: `Assistant Manager upgraded to level ${currentLevel + 1}: ${nextLevelInfo.name}`,
            updatedGameState: gameState
        };
    };

    /**
     * Downgrade assistant manager to previous level
     */
    const downgradeAssistantManager = (teamName: string): { success: boolean; message: string; updatedGameState?: GameState } => {
        const league = Object.values(gameState.leagues).find(l =>
            l.teams.some(t => t.name === teamName)
        );
        if (!league) return { success: false, message: 'Team not found' };

        const team = league.teams.find(t => t.name === teamName);
        if (!team) return { success: false, message: 'Team not found' };

        const currentLevel = team.assistantManager.level;
        if (currentLevel <= 1) return { success: false, message: 'Already at minimum level' };

        // Calculate severance (8 weeks of wages at current level)
        const currentLevelInfo = ASSISTANT_MANAGER_LEVELS[currentLevel - 1];
        const severance = currentLevelInfo.weeklyWages * 8;

        if (team.finances.balance < severance) {
            return { success: false, message: 'Insufficient funds for severance pay' };
        }

        // Deduct severance
        team.finances.balance -= severance;
        team.finances.history.push({
            season: gameState.season,
            week: gameState.currentWeek,
            event: `Downgraded Assistant Manager (Severance)`,
            amount: -severance
        });

        // Downgrade level
        team.assistantManager.level = Math.max(1, currentLevel - 1);

        // Generate new briefing from downgraded assistant manager
        const newBriefing = AssistantManagerBriefingService.generateWeeklyBriefing(gameState, team, league);
        if (!gameState.assistantManagerBriefingHistory) {
            gameState.assistantManagerBriefingHistory = [];
        }
        gameState.assistantManagerBriefingHistory.push(newBriefing);

        // Add news item about the downgrade
        const newManagerName = ASSISTANT_MANAGER_LEVELS[team.assistantManager.level - 1].name;
        addNewsItem(gameState, '📧 Assistant Manager Update', `Your new ${newManagerName} has provided an assessment following their appointment.`, 'general', undefined, { type: 'assistant', title: 'Assistant Manager' });

        return {
            success: true,
            message: `Assistant Manager downgraded to level ${team.assistantManager.level}`,
            updatedGameState: gameState
        };
    };

    /**
     * Get tactical recommendation from assistant manager
     */
    const getTacticalRecommendation = (teamName: string, nextOpponentTeam: Team | null) => {
        const league = Object.values(gameState.leagues).find(l =>
            l.teams.some(t => t.name === teamName)
        );
        if (!league) return null;

        const team = league.teams.find(t => t.name === teamName);
        if (!team) return null;

        return assistantManagerService.generateTacticalRecommendation(
            team as Team,
            nextOpponentTeam,
            team.assistantManager.level
        );
    };

    /**
     * Get transfer recommendations from assistant manager
     */
    const getTransferRecommendations = (teamName: string) => {
        const league = Object.values(gameState.leagues).find(l =>
            l.teams.some(t => t.name === teamName)
        );
        if (!league) return [];

        const team = league.teams.find(t => t.name === teamName);
        if (!team) return [];

        const recommendations = assistantManagerService.generateTransferRecommendations(
            team as Team,
            team.assistantManager.level
        );

        // Update stored suggestions with status tracking
        team.assistantManager.transferSuggestions = recommendations.map((rec, index) => ({
            id: `suggestion_${gameState.season}_${gameState.currentWeek}_${index}`,
            playerId: rec.playerId,
            playerName: rec.playerName,
            action: rec.action as 'sell' | 'buy' | 'scout' | 'free_agent',
            reason: rec.reason,
            suggestedPrice: rec.suggestedPrice,
            position: rec.position,
            skill: rec.skill,
            age: rec.age,
            week: gameState.currentWeek,
            status: 'pending' as const
        }));

        return recommendations;
    };

    /**
     * Accept a transfer suggestion and execute the action
     */
    const acceptSuggestion = (suggestionId: string): { success: boolean; message: string } => {
        const team = Object.values(gameState.leagues)
            .flatMap(l => l.teams)
            .find(t => t.name === gameState.playerTeamName);

        if (!team) return { success: false, message: 'Team not found' };

        const suggestionIndex = team.assistantManager.transferSuggestions.findIndex(s => s.id === suggestionId);
        if (suggestionIndex === -1) return { success: false, message: 'Suggestion not found' };

        const suggestion = team.assistantManager.transferSuggestions[suggestionIndex];

        // Mark as accepted
        suggestion.status = 'accepted';

        // Execute action based on suggestion type
        if (suggestion.action === 'sell') {
            // List player for sale
            const player = team.squad.find(p => p.id === suggestion.playerId);
            if (!player) return { success: false, message: 'Player not found' };

            player.isListed = true;
            player.listedDate = { season: gameState.season, week: gameState.currentWeek };
            player.askingPrice = suggestion.suggestedPrice || 100000;
            gameState.playersForSale.push(player.id);

            return { success: true, message: `${player.name} listed for sale at £${player.askingPrice.toLocaleString()}` };
        }

        // For buy/scout/free_agent actions, just mark as accepted
        // User will still need to manually execute the actual bid through Transfers screen
        // since we don't have enough context here to execute complex transfer actions
        return { success: true, message: `Action accepted. Please complete the transfer through the Transfers screen.` };
    };

    /**
     * Reject a transfer suggestion
     */
    const rejectSuggestion = (suggestionId: string): { success: boolean; message: string } => {
        const team = Object.values(gameState.leagues)
            .flatMap(l => l.teams)
            .find(t => t.name === gameState.playerTeamName);

        if (!team) return { success: false, message: 'Team not found' };

        const suggestionIndex = team.assistantManager.transferSuggestions.findIndex(s => s.id === suggestionId);
        if (suggestionIndex === -1) return { success: false, message: 'Suggestion not found' };

        const suggestion = team.assistantManager.transferSuggestions[suggestionIndex];
        suggestion.status = 'rejected';

        return { success: true, message: `Suggestion rejected` };
    };

    return {
        upgradeAssistantManager,
        downgradeAssistantManager,
        getTacticalRecommendation,
        getTransferRecommendations,
        acceptSuggestion,
        rejectSuggestion
    };
};
